import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Trash2, CheckCircle2, Circle, LogOut, Inbox, Loader2, RefreshCw } from "lucide-react";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rhawn_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    document.title = "Admin · Rhawn Auto Tag";
  }, []);

  if (!token) return <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  return <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />;
}

/* ---------- Login ---------- */
function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      onLogin(data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A192F] text-white flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <span className="w-9 h-9 rounded-full border border-[#C5A059] flex items-center justify-center text-[#C5A059] font-serif text-lg">R</span>
          <div className="leading-tight text-center">
            <div className="font-serif text-lg">Rhawn Admin</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059]">Message inbox</div>
          </div>
        </div>
        <form onSubmit={submit} className="border border-white/15 bg-white/[0.03] p-8" data-testid="admin-login-form">
          <div className="label-mini text-white/50">Password</div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="w-full mt-3 bg-transparent border-b border-white/25 focus:border-[#C5A059] outline-none py-3 text-lg tracking-widest"
            placeholder="•••••••••"
            data-testid="admin-password-input"
            required
          />
          <button
            type="submit"
            disabled={loading || !password}
            className="btn-gold mt-8 w-full justify-center"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <p className="mt-6 text-center text-white/40 text-xs">
          Access restricted. Contact info@rhawntag.com if locked out.
        </p>
      </div>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function Dashboard({ token, onLogout }) {
  const [data, setData] = useState({ leads: [], total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | contact | insurance_quote | unread
  const [active, setActive] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "unread") params.only_unread = true;
      else if (filter !== "all") params.type = filter;
      const { data } = await axios.get(`${API}/admin/leads`, { ...auth, params });
      setData(data);
      if (data.leads.length && !active) setActive(data.leads[0]);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load messages");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, token]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (lead, read) => {
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}`, { read }, auth);
      setData((d) => ({
        ...d,
        leads: d.leads.map((l) => (l.id === lead.id ? { ...l, read } : l)),
        unread: d.unread + (read ? -1 : 1),
      }));
      if (active?.id === lead.id) setActive({ ...active, read });
    } catch {
      toast.error("Could not update");
    }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete message from ${lead.name || lead.full_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, auth);
      setData((d) => ({
        ...d,
        leads: d.leads.filter((l) => l.id !== lead.id),
        total: d.total - 1,
        unread: d.unread - (lead.read ? 0 : 1),
      }));
      if (active?.id === lead.id) setActive(null);
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#0A192F]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A192F] text-white">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full border border-[#C5A059] flex items-center justify-center text-[#C5A059] font-serif">R</span>
            <div className="leading-tight">
              <div className="font-serif text-base">Rhawn Admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Message inbox</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="hidden sm:flex items-center gap-3">
              <Stat label="Total" value={data.total} />
              <Stat label="Unread" value={data.unread} accent />
            </div>
            <button onClick={load} className="p-2 rounded-full hover:bg-white/10 transition" aria-label="Refresh" data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/25 hover:border-[#C5A059] hover:text-[#C5A059] transition" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
        {/* Filters */}
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 pb-4 flex gap-2 overflow-x-auto no-scrollbar">
          {[
            { k: "all", l: "All" },
            { k: "unread", l: "Unread" },
            { k: "contact", l: "Contact" },
            { k: "insurance_quote", l: "Quotes" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider whitespace-nowrap transition ${
                filter === f.k ? "bg-[#C5A059] text-[#0A192F]" : "border border-white/25 text-white/80 hover:border-[#C5A059]"
              }`}
              data-testid={`admin-filter-${f.k}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-8 grid lg:grid-cols-12 gap-6">
        {/* Lead list */}
        <aside className="lg:col-span-5 xl:col-span-4">
          {loading && !data.leads.length ? (
            <SkeletonList />
          ) : data.leads.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-2" data-testid="admin-leads-list">
              {data.leads.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => setActive(l)}
                    className={`w-full text-left p-4 border transition-colors ${
                      active?.id === l.id
                        ? "bg-white border-[#C5A059]"
                        : "bg-white border-[#0A192F]/10 hover:border-[#C5A059]/50"
                    }`}
                    data-testid={`admin-lead-${l.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {!l.read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0" aria-label="Unread" />
                          )}
                          <span className="font-serif text-[15px] text-[#0A192F] truncate">
                            {l.name || l.full_name}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-[#0A192F]/60 truncate">
                          {l.type === "insurance_quote" ? "Insurance Quote · " : "Contact · "}
                          {l.subject || (l.vehicle_make ? `${l.vehicle_year || ""} ${l.vehicle_make} ${l.vehicle_model || ""}`.trim() : "—")}
                        </p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider shrink-0 ${
                        l.type === "insurance_quote" ? "text-[#C5A059]" : "text-[#0A192F]/50"
                      }`}>
                        {l.type === "insurance_quote" ? "Quote" : "Msg"}
                      </span>
                    </div>
                    <div className="mt-2 text-[11px] text-[#0A192F]/45 font-mono-tab">
                      {formatDate(l.created_at)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Detail panel */}
        <main className="lg:col-span-7 xl:col-span-8">
          {active ? (
            <LeadDetail lead={active} onToggleRead={(v) => markRead(active, v)} onDelete={() => removeLead(active)} />
          ) : (
            <div className="border border-[#0A192F]/10 bg-white p-10 text-center text-[#0A192F]/50" data-testid="admin-empty-detail">
              Select a message to view details.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/* ---------- Lead detail panel ---------- */
function LeadDetail({ lead, onToggleRead, onDelete }) {
  const isQuote = lead.type === "insurance_quote";
  const name = lead.name || lead.full_name;
  const vehicle = [lead.vehicle_year, lead.vehicle_make, lead.vehicle_model].filter(Boolean).join(" ");
  return (
    <article className="border border-[#0A192F]/10 bg-white p-6 md:p-8" data-testid="admin-lead-detail">
      <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-[#0A192F]/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059]">
            {isQuote ? "Insurance Quote Request" : "Contact Message"}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mt-2">{name}</h2>
          <p className="mt-1 text-xs text-[#0A192F]/55 font-mono-tab">
            {formatDate(lead.created_at, true)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onToggleRead(!lead.read)}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[#0A192F]/12 hover:border-[#C5A059] text-xs rounded-full transition"
            data-testid="admin-toggle-read"
          >
            {lead.read ? <CheckCircle2 className="w-3.5 h-3.5 text-[#C5A059]" /> : <Circle className="w-3.5 h-3.5" />}
            {lead.read ? "Read" : "Mark as read"}
          </button>
          <button
            onClick={onDelete}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[#0A192F]/12 hover:border-red-500 hover:text-red-500 text-xs rounded-full transition"
            data-testid="admin-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-6">
        <Field label="Email">
          <a href={`mailto:${lead.email}`} className="link-under" data-testid="admin-email-link">
            <Mail className="w-3.5 h-3.5 inline mr-1.5" />{lead.email}
          </a>
        </Field>
        {lead.phone && (
          <Field label="Phone">
            <a href={`tel:${lead.phone}`} className="link-under" data-testid="admin-phone-link">
              <Phone className="w-3.5 h-3.5 inline mr-1.5" />{lead.phone}
            </a>
          </Field>
        )}
        {isQuote ? (
          <>
            {vehicle && <Field label="Vehicle">{vehicle}</Field>}
            {lead.coverage && <Field label="Coverage">{lead.coverage}</Field>}
          </>
        ) : (
          lead.subject && <Field label="Subject">{lead.subject}</Field>
        )}
      </div>

      <div className="mt-8">
        <div className="label-mini">{isQuote ? "Notes" : "Message"}</div>
        <p className="mt-3 text-[#0A192F]/85 leading-relaxed whitespace-pre-wrap border-l-2 border-[#C5A059] pl-5 text-[15px]">
          {(isQuote ? lead.notes : lead.message) || "—"}
        </p>
      </div>
    </article>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="label-mini">{label}</div>
      <div className="mt-2 text-[15px] text-[#0A192F]">{children}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</div>
      <div className={`font-mono-tab text-lg leading-none mt-0.5 ${accent ? "text-[#C5A059]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-2 animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="h-20 bg-white border border-[#0A192F]/10" />
      ))}
    </ul>
  );
}

function EmptyState() {
  return (
    <div className="border border-[#0A192F]/10 bg-white p-10 text-center" data-testid="admin-empty-list">
      <Inbox className="w-8 h-8 mx-auto text-[#C5A059]" />
      <p className="mt-4 font-serif text-lg text-[#0A192F]">No messages here yet.</p>
      <p className="mt-2 text-sm text-[#0A192F]/60">
        Submissions from the website will appear here automatically.
      </p>
    </div>
  );
}

function formatDate(iso, long = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: long ? "numeric" : undefined,
    hour: "numeric",
    minute: "2-digit",
  });
}

// Expose SITE for downstream chunks (some bundlers tree-shake unused imports otherwise)
void SITE;
